import React, { useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStaticProps } from '../lib/getStatic'
import { NextSeo } from 'next-seo'
import { HeroIcons } from '../lib/utils/heroIcons'

interface LegalsTopic {
  title: string
  paragraphs: {
    title: string
    text: any[]
  }[]
}

export default function PrivacyPolicyPage() {
  const { t } = useTranslation('privacy-policy')
  const legalsTopics = useMemo<LegalsTopic[]>(
    () =>
      (t('topics', { returnObjects: true }) as LegalsTopic[]).map(
        (legalsTopic: LegalsTopic) => {
          legalsTopic.paragraphs = legalsTopic.paragraphs.map(
            (paragraph: any) => {
              paragraph.text = paragraph.text.map((text: any) => {
                return text
              })
              return paragraph
            },
          )
          return legalsTopic
        },
      ),
    [t],
  )

  return (
    <>
      <NextSeo
        title={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />
      <main className="relative overflow-hidden bg-[#f4f6ff]">
        <section className="bg-gradient-box relative overflow-hidden">
          <div className="pointer-events-none absolute -left-32 top-24 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.16)_0%,transparent_70%)]" />
          <div className="pointer-events-none absolute -right-24 -top-10 h-[460px] w-[460px] rounded-full bg-[radial-gradient(circle,rgba(188,174,255,0.22)_0%,transparent_72%)]" />

          <div className="relative mx-auto w-full px-5 pb-16 pt-28 tablet:px-16 tablet:pb-20 tablet:pt-32 desktop:px-24 2xl:px-44">
            <h1 className="w-full font-semibold text-white text-shelters-section-title">
              {t('title')}
            </h1>
            <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-shelters-kicker text-white/75 backdrop-blur-md">
              <HeroIcons.ClockIcon className="h-4 w-4" />
              <span>
                {t('lastUpdate')}{' '}
                <strong className="font-semibold text-white">
                  {t('updateDate')}
                </strong>
              </span>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute left-[-10%] top-16 h-96 w-96 rounded-full bg-[radial-gradient(circle,rgba(165,181,255,0.28)_0%,transparent_70%)] blur-2xl" />
          <div className="pointer-events-none absolute right-[-8%] top-[45%] h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(141,109,202,0.16)_0%,transparent_72%)] blur-2xl" />

          <div className="relative mx-auto w-full px-5 pb-24 pt-12 tablet:px-16 tablet:pb-32 tablet:pt-16 desktop:px-24 2xl:px-44">
            <div className="flex flex-col gap-5 tablet:gap-6">
              {legalsTopics.map((topic, index) => (
                <article
                  key={index}
                  className="relative overflow-hidden rounded-container border border-white/60 bg-white/65 p-6 shadow-[0_18px_44px_rgba(31,27,99,0.10)] backdrop-blur-xl tablet:p-8"
                >
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.9),transparent)]" />

                  <div className="flex items-center gap-4">
                    <span className="inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-inner-card bg-gradient px-2.5 text-shelters-kicker font-semibold text-white shadow-[0_10px_24px_rgba(51,40,154,0.24)]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h2 className="font-semibold text-sheltersDarkBlue text-shelters-benefit-title">
                      {topic.title}
                    </h2>
                  </div>

                  <div className="mt-5 space-y-5">
                    {topic.paragraphs.map((p, i) => (
                      <div key={i}>
                        {p.title && (
                          <h3 className="mb-2 text-shelters-body font-semibold text-sheltersBlue">
                            {p.title}
                          </h3>
                        )}
                        <div className="space-y-3">
                          {p.text.map((paragraph, j) => (
                            <p
                              key={j}
                              className="text-shelters-body leading-[1.65] text-[#4c5675]"
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

const getStaticProps = makeStaticProps([
  'common',
  'index',
  'navbar',
  'footer',
  'privacy-policy',
  'modals',
  'newsletter',
])
export { getStaticProps, PrivacyPolicyPage }
